/**
 * WebHID glue for the Zebra DS3608-SR in **USB HID POS / USB OPOS
 * (Handheld)** mode. The scanner must be switched to this mode from its
 * default HID Keyboard Emulation by scanning the setup barcode in the
 * DS3608 product reference guide — keyboard mode is the wrong shape for
 * long QR payloads (drops chars, needs focus).
 *
 * Wire format (per Zebra USB HID POS input report, Report ID 0x02)
 * ----------------------------------------------------------------
 *   Byte 0       : DataLength — number of valid data bytes that follow
 *                  in this report (1…63 for full-speed USB HID, the rest
 *                  of the report is padding we ignore).
 *   Bytes 1..N   : Decoded barcode data bytes (ASCII for text symbologies
 *                  like QR Code carrying base64). Long scans span multiple
 *                  reports; each report repeats the length-prefix layout.
 *
 * There is no continuation / "last fragment" flag in this format — the
 * scanner simply stops sending reports once the scan is complete. We
 * reassemble by timing: any pause longer than `IDLE_FLUSH_MS` between
 * reports finalises the current scan. That latency is part of why
 * end-of-scan feels like ~100 ms rather than instant.
 *
 * Baseline diagnostic — a `[hid] opened …` line naming the device and its
 * HID collections fires on every open, and the first input report is
 * always logged as hex so "is anything arriving?" is visible without any
 * flag. For the full per-report dump set
 * `localStorage.setItem('hid.debug', '1')` and reload; each report and
 * each flushed payload is then written (via `console.warn` — the
 * project's `no-console` policy only permits `warn`/`error`) as hex +
 * ASCII so we can diagnose unexpected scanner output live.
 *
 * If the device's collections include a keyboard usage we emit a loud
 * warning — Chrome refuses to deliver input reports for protected HID
 * usages, which is the silent failure mode when a scanner is still in
 * HID Keyboard Emulation rather than HID-POS.
 *
 * Browser support: Chromium only. Caller must check `isWebHidSupported()`.
 */

// Symbol Technologies / Zebra. All Zebra DS-series scanners use this VID.
const ZEBRA_VENDOR_ID = 0x05e0

/**
 * Per-device promise chain that serialises state-changing operations
 * (open / close). WebHID rejects overlapping state transitions on the
 * same device with `InvalidStateError`, which is what React StrictMode's
 * double-invoke of mount effects triggers: the first mount's `open()` is
 * still in flight when the second mount starts another one. Chaining
 * every transition through here makes the second call wait for the
 * first to land (whichever direction) before it proceeds.
 */
const deviceTransitions = new WeakMap<HIDDevice, Promise<unknown>>()

function serialiseOnDevice<T>(device: HIDDevice, fn: () => Promise<T>): Promise<T> {
  const prev = deviceTransitions.get(device) ?? Promise.resolve()
  const next = prev.then(fn, fn)
  // Keep the chain alive even if `fn` rejects so the *next* caller waits
  // for the failure to settle rather than piling on.
  deviceTransitions.set(
    device,
    next.catch(() => undefined),
  )
  return next
}

const IDLE_FLUSH_MS = 80

/**
 * HID Usage Pages / Usages we care about when classifying a Zebra scanner's
 * current mode. See `classifyZebraMode` for how these are used.
 *
 * - Generic Desktop + Keyboard = HID Keyboard Emulation mode. Chrome
 *   refuses to deliver input reports for protected usages, so we detect
 *   and surface this.
 * - Barcode Scanner (0x8c) = USB HID POS / USB OPOS Handheld — the mode
 *   our reader targets. PID is typically 0x0207.
 * - Zebra vendor-defined (0xFF00-0xFFFF) with usage 0x4b00 = SNAPI
 *   (Symbol Native API / SSI over USB HID). PID is 0x1300. We don't
 *   speak SSI here; scanners in this mode need a mode-switch setup scan.
 */
const GENERIC_DESKTOP = 0x01
const KEYBOARD_USAGE = 0x06
const BARCODE_SCANNER_PAGE = 0x8c
const ZEBRA_SNAPI_USAGE = 0x4b00

type ZebraMode = 'hid-pos' | 'keyboard' | 'snapi' | 'unknown'

export function classifyZebraMode(device: HIDDevice): ZebraMode {
  for (const c of device.collections) {
    if (c.usagePage === BARCODE_SCANNER_PAGE) return 'hid-pos'
    if (c.usagePage === GENERIC_DESKTOP && c.usage === KEYBOARD_USAGE) return 'keyboard'
    // Zebra SNAPI lives in the vendor-defined page range (0xFF00-0xFFFF)
    // with a specific scanner usage. Treat any vendor-defined page with
    // that usage as SNAPI — different firmware revs reuse slightly
    // different pages.
    if (c.usagePage !== undefined && c.usagePage >= 0xff00 && c.usage === ZEBRA_SNAPI_USAGE) {
      return 'snapi'
    }
  }
  return 'unknown'
}

export class WrongModeError extends Error {
  constructor(
    message: string,
    readonly mode: ZebraMode,
    readonly device: HIDDevice,
  ) {
    super(message)
    this.name = 'WrongModeError'
  }
}

/**
 * Remove the browser-level pairing for a device. Called when the user
 * confirms the scanner was reconfigured to a new USB mode — the old
 * HIDDevice points at a PID that the physical scanner no longer enumerates
 * as, so holding the permission prevents the picker from ever showing the
 * new device.
 */
export async function forgetDevice(device: HIDDevice): Promise<void> {
  if (typeof device.forget === 'function') {
    await device.forget()
  }
}

/**
 * True when the `hid.debug` localStorage flag is set to `'1'`. Exported so
 * downstream code (the decode handler in `DecodeLabelPage`) can attach
 * its own debug dumps to the same flag — users then flip one switch to
 * see raw reports *and* the decoded envelope side by side.
 */
export function isHidDebugEnabled(): boolean {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem('hid.debug') === '1'
  } catch (err) {
    console.warn('[hid] localStorage read failed while checking debug flag', err)
    return false
  }
}

/**
 * Informational log gated on `hid.debug`. Uses `console.log` so the
 * output doesn't get the yellow warning icon in DevTools — these lines
 * are diagnostics, not anomalies. One eslint-disable here keeps the
 * project-wide `no-console` rule tight everywhere else.
 */
export function debugInfo(msg: string): void {
  if (!isHidDebugEnabled()) return
  // eslint-disable-next-line no-console
  console.log(msg)
}

function debugLog(tag: string, bytes: Uint8Array, note?: string): void {
  if (!isHidDebugEnabled()) return
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join(' ')
  const ascii = [...bytes]
    .map((b) => (b >= 0x20 && b <= 0x7e ? String.fromCharCode(b) : '.'))
    .join('')
  debugInfo(
    `[hid] ${tag}${note ? ` (${note})` : ''} len=${bytes.length}\n  hex: ${hex}\n  ascii: ${ascii}`,
  )
}

export function isWebHidSupported(): boolean {
  return typeof navigator !== 'undefined' && 'hid' in navigator
}

function hid(): HID {
  if (!isWebHidSupported()) {
    throw new Error('WebHID is not supported in this browser')
  }
  return navigator.hid
}

/** Devices the user has previously granted permission for. */
export async function getPairedZebraScanners(): Promise<HIDDevice[]> {
  const all = await hid().getDevices()
  return all.filter((d) => d.vendorId === ZEBRA_VENDOR_ID)
}

/**
 * Prompt the user to pick a scanner. Must be called from a user gesture
 * (click). Filters to Zebra VID by default; pass `any: true` to let the
 * user pick any HID device for debugging an unknown scanner.
 */
export async function requestZebraScanner(opts: { any?: boolean } = {}): Promise<HIDDevice | null> {
  const filters = opts.any ? [] : [{ vendorId: ZEBRA_VENDOR_ID }]
  const picked = await hid().requestDevice({ filters })
  return picked[0] ?? null
}

export interface HidScannerHandle {
  device: HIDDevice
  /** Stop listening and close the device. Safe to call multiple times. */
  close(): Promise<void>
}

/**
 * Open the device, listen for input reports, and call `onScan` once the
 * scanner has emitted a complete barcode. Errors during open/close are
 * logged and surfaced via `onError`; per-report parse glitches are
 * suppressed (we just keep buffering until the next idle flush).
 */
interface Listener {
  onScan: (text: string) => void
  onError?: (err: Error) => void
}

interface Session {
  device: HIDDevice
  listeners: Set<Listener>
  teardown: () => void
}

/**
 * Grace window between "last listener removed" and "actually close the
 * USB device". Long enough for React StrictMode's mount → cleanup → mount
 * pattern (tens of ms in practice) to slip through without churning the
 * scanner; short enough that real navigation-away releases the device
 * before the user has finished the next page transition.
 */
const SESSION_RELEASE_DELAY_MS = 300

/**
 * One live session per device, shared across all callers. React
 * StrictMode's mount → cleanup → mount pattern otherwise churns the USB
 * state machine (open → close → open in tens of ms) which some scanner
 * firmware doesn't survive — reports silently stop arriving. With the
 * session cache, StrictMode's cleanup just removes a listener; the
 * device stays open and data keeps flowing.
 */
const sessionPromises = new Map<HIDDevice, Promise<Session>>()

/**
 * Pending releases keyed by device. When the last listener detaches we
 * schedule a deferred close; a re-open within the grace window cancels
 * the timer and reuses the existing session.
 */
const releaseTimers = new WeakMap<HIDDevice, ReturnType<typeof setTimeout>>()

function cancelPendingRelease(device: HIDDevice): void {
  const timer = releaseTimers.get(device)
  if (timer !== undefined) {
    clearTimeout(timer)
    releaseTimers.delete(device)
  }
}

async function tearDownSession(device: HIDDevice, session: Session): Promise<void> {
  releaseTimers.delete(device)
  sessionPromises.delete(device)
  session.teardown()
  await serialiseOnDevice(device, async () => {
    if (device.opened) await device.close()
  })
  debugInfo(`[hid] released "${device.productName}"`)
}

export async function openHidScanner(
  device: HIDDevice,
  callbacks: Listener,
): Promise<HidScannerHandle> {
  // A re-open during the grace window means the same logical user is
  // still here — keep the existing session.
  cancelPendingRelease(device)
  let pending = sessionPromises.get(device)
  if (!pending) {
    pending = createSession(device)
    sessionPromises.set(device, pending)
    // If creation fails, drop the cache entry so the next caller retries.
    pending.catch(() => sessionPromises.delete(device))
  }
  const session = await pending
  session.listeners.add(callbacks)
  let unregistered = false
  return {
    device,
    close: () => {
      if (unregistered) return Promise.resolve()
      unregistered = true
      session.listeners.delete(callbacks)
      // Defer the real device close: if React StrictMode is about to
      // re-mount the consumer in a few ms, openHidScanner's
      // `cancelPendingRelease` will short-circuit the timer. If no new
      // listener appears within the grace window — the user navigated
      // away — actually release the USB handle so the OS / browser can
      // reclaim the scanner.
      if (session.listeners.size > 0) return Promise.resolve()
      cancelPendingRelease(device)
      const timer = setTimeout(() => {
        if (session.listeners.size > 0) {
          releaseTimers.delete(device)
          return
        }
        void tearDownSession(device, session).catch((err: unknown) => {
          console.warn('[hid] release failed', err)
        })
      }, SESSION_RELEASE_DELAY_MS)
      releaseTimers.set(device, timer)
      return Promise.resolve()
    },
  }
}

async function createSession(device: HIDDevice): Promise<Session> {
  await serialiseOnDevice(device, async () => {
    if (!device.opened) await device.open()
  })

  const collectionsSummary = device.collections
    .map(
      (c) =>
        `usagePage=0x${(c.usagePage ?? 0).toString(16)} usage=0x${(c.usage ?? 0).toString(16)} inputs=${c.inputReports?.length ?? 0} outputs=${c.outputReports?.length ?? 0}`,
    )
    .join(' | ')
  const mode = classifyZebraMode(device)
  debugInfo(
    `[hid] opened "${device.productName}" vid=0x${device.vendorId.toString(16)} pid=0x${device.productId.toString(16)} mode=${mode} collections=[${collectionsSummary || 'none'}]`,
  )

  const listeners = new Set<Listener>()
  const fan = (kind: 'scan' | 'error', value: string | Error) => {
    for (const l of listeners) {
      try {
        if (kind === 'scan') l.onScan(value as string)
        else l.onError?.(value as Error)
      } catch (err) {
        console.warn('[hid] listener threw', err)
      }
    }
  }

  if (mode === 'hid-pos') return attachHidPos(device, listeners, fan)
  if (mode === 'snapi') return attachSnapi(device, listeners, fan)

  const hint =
    mode === 'keyboard'
      ? 'Scanner is in HID Keyboard Emulation mode. Switch it to USB HID POS / USB OPOS Handheld and re-pair.'
      : `Unrecognised HID mode (collections=[${collectionsSummary}]). Reconfigure to USB HID POS / USB OPOS Handheld and re-pair.`
  debugInfo(`[hid] ${hint}`)
  await serialiseOnDevice(device, async () => {
    if (device.opened) await device.close()
  })
  throw new WrongModeError(hint, mode, device)
}

type Fan = (kind: 'scan' | 'error', value: string | Error) => void

/**
 * USB HID POS / USB OPOS Handheld: reports are length-prefixed raw scan
 * bytes. No handshake. Long scans span multiple reports; we reassemble by
 * idle-time since there's no end-of-packet flag in this layout.
 */
function attachHidPos(device: HIDDevice, listeners: Set<Listener>, fan: Fan): Session {
  debugInfo('[hid] HID POS handler enabled')
  let buffer: number[] = []
  let idleTimer: ReturnType<typeof setTimeout> | null = null
  let reportsSeen = 0

  const flush = () => {
    idleTimer = null
    if (buffer.length === 0) return
    const raw = Uint8Array.from(buffer)
    debugLog('hidpos flush raw', raw)
    const text = decodePayload(raw)
    buffer = []
    if (text.length > 0) fan('scan', text)
  }
  const armIdle = () => {
    if (idleTimer !== null) clearTimeout(idleTimer)
    idleTimer = setTimeout(flush, IDLE_FLUSH_MS)
  }

  const handleReport = (event: HIDInputReportEvent) => {
    const view = event.data
    const bytes = new Uint8Array(view.buffer, view.byteOffset, view.byteLength)
    reportsSeen++
    if (reportsSeen === 1) {
      const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join(' ')
      debugInfo(
        `[hid] first report id=0x${event.reportId.toString(16)} len=${bytes.length} hex=${hex}`,
      )
    }
    debugLog('report', bytes, `id=0x${event.reportId.toString(16)}`)
    const fragment = extractFragment(bytes)
    for (const b of fragment) buffer.push(b)
    armIdle()
  }
  device.addEventListener('inputreport', handleReport)

  return {
    device,
    listeners,
    teardown: () => {
      if (idleTimer !== null) clearTimeout(idleTimer)
      device.removeEventListener('inputreport', handleReport)
    },
  }
}

/**
 * Zebra SNAPI / SSI-over-USB-HID. Observed wire format on a DS3608-SR:
 *
 *   Byte 0          : fragment byte count (e.g. 0x40 = 64 for a full report)
 *   Byte 1          : total SSI message size across fragments
 *   Byte 2          : SSI opcode — 0xF3 (DECODE_DATA) for scan payloads
 *   Byte 3          : SSI source — 0x00 from scanner
 *   Byte 4          : SSI status — bit 1 = "more packets follow"
 *   Byte 5          : (first fragment only) barcode type (0x0B = QR Code)
 *   Bytes 6..       : barcode data (first fragment)
 *
 * Continuation fragments skip the opcode/source/status/barcode-type
 * preamble and carry bytes 2..end as raw data. CRC bytes land at the very
 * end of the reassembled stream and are stripped by `decodePayload`
 * along with other non-printable bytes.
 *
 * ACKs are intentionally *not* sent: the diagnostic test page received
 * scans cleanly without them, and a malformed ACK from us seems to make
 * the scanner clam up. If reliability ever becomes an issue we can add
 * a correctly HID-wrapped ACK back here.
 */
function attachSnapi(device: HIDDevice, listeners: Set<Listener>, fan: Fan): Session {
  debugInfo('[hid] SNAPI handler enabled')

  let buffer: number[] = []
  let idleTimer: ReturnType<typeof setTimeout> | null = null
  let reportsSeen = 0

  const flush = () => {
    idleTimer = null
    if (buffer.length === 0) return
    const raw = Uint8Array.from(buffer)
    debugLog('snapi flush raw', raw)
    const text = decodePayload(raw)
    buffer = []
    if (text.length > 0) fan('scan', text)
  }
  const armIdle = () => {
    if (idleTimer !== null) clearTimeout(idleTimer)
    idleTimer = setTimeout(flush, IDLE_FLUSH_MS)
  }

  const connectHandler = (e: HIDConnectionEvent) => {
    if (e.device === device) debugInfo('[hid] device connected')
  }
  const disconnectHandler = (e: HIDConnectionEvent) => {
    if (e.device === device) debugInfo('[hid] device disconnected')
  }
  navigator.hid.addEventListener('connect', connectHandler)
  navigator.hid.addEventListener('disconnect', disconnectHandler)

  const handleReport = (event: HIDInputReportEvent) => {
    const view = event.data
    const bytes = new Uint8Array(view.buffer, view.byteOffset, view.byteLength)
    reportsSeen++
    if (reportsSeen === 1) {
      const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join(' ')
      debugInfo(
        `[hid] first SNAPI report id=0x${event.reportId.toString(16)} len=${bytes.length} hex=${hex}`,
      )
    }
    debugLog('snapi report', bytes, `id=0x${event.reportId.toString(16)}`)

    if (bytes.length < 8) {
      armIdle()
      return
    }
    // Empirical Zebra SNAPI-over-HID fragment framing as observed on the
    // DS3608-SR carrying a multi-fragment QR decode:
    //   bytes 0..3                       : 4-byte HID+SSI header
    //   bytes 4..fragmentSize-4          : payload
    //   bytes fragmentSize-3..fragmentSize-1
    //     : per-fragment trailer "<flag> 33 0b" where flag = 0x10 (more
    //       fragments follow) or 0x00 (final fragment).
    // Small single-fragment decodes don't carry the trailer; we detect it
    // by byte pattern so we don't accidentally clip three bytes of real
    // payload off a short barcode.
    const fragmentSize = Math.min(bytes[0]!, bytes.length)
    const dataStart = 4
    const hasTrailer =
      fragmentSize >= 7 &&
      bytes[fragmentSize - 2] === 0x33 &&
      bytes[fragmentSize - 1] === 0x0b &&
      (bytes[fragmentSize - 3] === 0x10 || bytes[fragmentSize - 3] === 0x00)
    const dataEnd = hasTrailer ? fragmentSize - 3 : fragmentSize
    for (let i = dataStart; i < dataEnd; i++) buffer.push(bytes[i]!)
    armIdle()
  }
  device.addEventListener('inputreport', handleReport)

  return {
    device,
    listeners,
    teardown: () => {
      if (idleTimer !== null) clearTimeout(idleTimer)
      device.removeEventListener('inputreport', handleReport)
      navigator.hid.removeEventListener('connect', connectHandler)
      navigator.hid.removeEventListener('disconnect', disconnectHandler)
    },
  }
}

// --- SSI protocol helpers --------------------------------------------------

/**
 * CRC-16-CCITT with init 0xFFFF, poly 0x1021, no reflection, no final XOR.
 * Exported so the SSI frame builders in `hid-test.html` and any future
 * host→scanner commands (ACK, SCAN_ENABLE, PARAM_SEND) have a tested
 * implementation to reach for. Currently unused by the in-app path — we
 * found the Zebra DS3608 happily streams decodes without host ACKs.
 */
export function crcCcitt(bytes: Uint8Array): number {
  let crc = 0xffff
  for (const b of bytes) {
    crc ^= b << 8
    for (let i = 0; i < 8; i++) {
      crc = (crc & 0x8000) === 0 ? (crc << 1) & 0xffff : ((crc << 1) ^ 0x1021) & 0xffff
    }
  }
  return crc
}

/**
 * Pull the valid data bytes out of one Zebra HID-POS scan-data report.
 * Format: byte 0 = declared length, bytes 1..length = data, rest is
 * padding. If the declared length looks bogus (0 or larger than the
 * report can hold), fall back to the whole report with trailing nulls
 * stripped — that way a mis-configured scanner still produces *something*
 * the debug log can show.
 */
function extractFragment(bytes: Uint8Array): Uint8Array {
  if (bytes.length === 0) return bytes
  const declaredLen = bytes[0]!
  if (declaredLen > 0 && declaredLen <= bytes.length - 1) {
    return bytes.slice(1, 1 + declaredLen)
  }
  return trimTrailingZeros(bytes)
}

function trimTrailingZeros(bytes: Uint8Array): Uint8Array {
  let end = bytes.length
  while (end > 0 && bytes[end - 1] === 0) end--
  return bytes.slice(0, end)
}

/**
 * QR payloads we care about (base64 / `c1.` / raw JSON) are all printable
 * ASCII. Decode as UTF-8 with `fatal: false` so leading code-ID prefixes
 * or stray suffix bytes decode to control chars we then strip. Newline
 * (0x0a) is kept since users sometimes configure it as a scan suffix;
 * the surrounding `.trim()` clears it again.
 */
function decodePayload(bytes: Uint8Array): string {
  const text = new TextDecoder('utf-8', { fatal: false }).decode(bytes)
  let out = ''
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i)
    if (code === 0x0a) {
      out += text[i]
      continue
    }
    if (code < 0x20 || code === 0x7f) continue
    out += text[i]
  }
  return out.trim()
}
